import React from "react";

import {
  roleColorFor,
} from "../../core/shifts/shiftPresentation";
import {
  getShiftOperationalSummary,
} from "../../core/shifts/shiftOperationalSummary";

import {
  computeShiftActivationUrgency as computeShiftLifecycle,
  computeShiftCountdown,
} from "../../core/shifts/shiftLifecycle";

function toMillis(v) {
  if (!v) return NaN;
  try {
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (typeof v === "number") return v;
    return new Date(v).getTime();
  } catch {
    return NaN;
  }
}

function hasShiftEnded(shift, nowMs = Date.now()) {
  const endMs = toMillis(shift?.endTime);
  return Number.isFinite(endMs) && nowMs >= endMs;
}

function fmtShortRange(startV, endV, shift = null) {
  if (shift?.isFlexibleTime) return "Flexible";

  const s = toMillis(startV);
  const e = toMillis(endV);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return "—";

  const timeZone = shift?.timeZone || shift?.timezone;
  if (!timeZone) return "Timezone unavailable";
  const start = new Date(s);
  const end = new Date(e);

  return `${start.toLocaleTimeString([], {timeZone,
    hour: "numeric",
    minute: "2-digit",
  })} → ${end.toLocaleTimeString([], {timeZone,
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function getAssignedWorkerUid(shift) {
  if (!shift) return "";
  if (shift.assignedWorkerUid) return shift.assignedWorkerUid;
  if (Array.isArray(shift.assignedWorkerUids) && shift.assignedWorkerUids.length > 0) {
    return shift.assignedWorkerUids[0];
  }
  return "";
}

function getShiftMode(shift) {
  return String(shift?.mode || shift?.assignMode || "pool").toLowerCase();
}

function shortNameFromEmail(email) {
  const local = String(email || "").split("@")[0] || "";
  if (!local) return "";
  return local.length > 14 ? `${local.slice(0, 14)}…` : local;
}

function getWorkerLabel(shift, workersByUid) {
  const uid = getAssignedWorkerUid(shift);
  if (!uid) return "";

  const worker = workersByUid?.get?.(uid);
  if (!worker) return uid.length > 10 ? `${uid.slice(0, 10)}…` : uid;

  return (
    worker.displayName ||
    worker.fullName ||
    worker.name ||
    shortNameFromEmail(worker.email) ||
    worker.email ||
    uid.slice(0, 10)
  );
}


export default function PlannerShiftCard({
  shift,
  compactHeight = 108,
  now = Date.now(),
  selectedShiftId = "",
  selectedShiftIds = [],
  toggleShiftSelection = () => {},
  setSelectedShiftId = () => {},
  workersByUid = new Map(),
}) {
  const roleName = String(shift?.role || "").toUpperCase().trim();
  const colors = roleColorFor(roleName || "UNKNOWN");

   const operational =
    getShiftOperationalSummary(
      shift, now
    );

  const assignedUid =
    operational.assignedWorkerUid;

  const workerLabel =
    getWorkerLabel(
      shift,
      workersByUid
    );

  const statusName =
    operational.status;

  const modeName =
    getShiftMode(shift);

  const ended = hasShiftEnded(shift, now);
   const isArrived =
    operational.arrived;

  const isCompleted =
    statusName === "completed";

  const isNoShow =
    statusName === "no_show";

  const isClosedBad =
    [
      "no_show",
      "cancelled",
      "canceled",
    ].includes(statusName);

  const isAwaitingAcceptance =
    operational.awaitingAcceptance;

  const isCommitted =
    operational.confirmedCoverage &&
    !isCompleted;

  const isFloating =
    !ended &&
    [
      "open",
      "offered",
      "requested",
      "time_required",
    ].includes(statusName);

  const selected = selectedShiftIds.includes(shift.id);
  const active = selectedShiftId === shift.id;

  const finalHeight = ended || isCompleted ? 86 : compactHeight;
   const isPriorityFilled =
    operational.confirmedCoverage &&
    !isCompleted;

const visualHeight = isPriorityFilled
  ? finalHeight + 8
  : finalHeight;

   const lifecycle = ended
    ? "ended"
    : [
        "completed",
        "arrived",
        "tracking",
        "activated",
        "assigned",
      ].includes(statusName)
    ? statusName
    : computeShiftLifecycle(
        shift,
        now,
        2
      );

  const countdown = ended
    ? { principal: "Ended", secondary: "" }
    : computeShiftCountdown(shift, now, 2);

    const startMs = toMillis(shift.startTime);
const nowMs = Date.now();

const minsToStart = Math.floor((startMs - nowMs) / 60000);

const isUrgent =
  !ended &&
  ["open", "offered", "requested"].includes(statusName) &&
  minsToStart <= 120;

const isCritical =
  !ended &&
  ["open", "offered", "requested"].includes(statusName) &&
  minsToStart <= 45;

const isOverdueOpen =
  !ended &&
  ["open", "offered", "requested"].includes(statusName) &&
  minsToStart < 0;

const background = isNoShow
  ? "#fee2e2"
  : ended || isCompleted
  ? "#f1f5f9"
  : isFloating
  ? `repeating-linear-gradient(
      135deg,
      ${colors.color}20,
      ${colors.color}20 8px,
      rgba(255,255,255,.98) 8px,
      rgba(255,255,255,.98) 16px
    )`
  : isArrived
  ? `linear-gradient(135deg, ${colors.color}22 0%, ${colors.color}52 100%)`
  : isCommitted
  ? `linear-gradient(135deg, ${colors.color}30 0%, ${colors.color}70 100%)`
  : "#ffffff";

  const statusBg =
    isArrived || isCompleted
      ? "#dcfce7"
      : statusName === "tracking"
      ? "#ede9fe"
      : isAwaitingAcceptance
      ? "#fff7ed"
      : statusName === "assigned" ||
        statusName === "activated"
      ? "#eff6ff"
      : statusName === "offered"
      ? "#fff7ed"
      : "#f8fafc";

  const statusColor =
    isArrived || isCompleted
      ? "#166534"
      : statusName === "tracking"
      ? "#6d28d9"
      : isAwaitingAcceptance
      ? "#9a3412"
      : statusName === "assigned" ||
        statusName === "activated"
      ? "#1d4ed8"
      : statusName === "offered"
      ? "#9a3412"
      : "#334155";

  const footerText = ended
    ? "Ended"
    : ["completed", "arrived", "activated", "assigned"].includes(statusName)
      ? countdown?.principal
      : String(lifecycle || "").toUpperCase() === "PENDING"
        ? countdown?.principal
        : countdown?.principal || "";

const isTracking =
  operational.enRoute;

const trackingMeta =
  isTracking
    ? shift?.trackingLastPingAt
      ? `Live • Last ping ${new Date(
          toMillis(shift.trackingLastPingAt)
        ).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })}`
      : "Live tracking"
    : "";

const trackingDistance =
  isTracking && shift?.distanceMiles != null
    ? `${Number(shift.distanceMiles).toFixed(1)} mi away`
    : "";
    

  return <article className={'schedule-shift-card'+(selected || active ? ' is-selected' : '')+(ended ? ' is-ended' : '')}
    style={{'--shift-accent':colors.color, minHeight:Math.max(112,visualHeight)}}
    aria-label={roleName+' '+fmtShortRange(shift.startTime,shift.endTime,shift)+' '+(operational.operationalLabel || statusName)}
    onClick={e => {e.stopPropagation(); setSelectedShiftId(shift.id);}}>
    <div className="schedule-shift-heading">
      <input type="checkbox" aria-label={'Select '+roleName+' shift'} checked={selected}
        onChange={() => toggleShiftSelection(shift.id)} onClick={e => e.stopPropagation()} />
      <strong>{roleName.replace(/_/g,' ') || 'SHIFT'}</strong>
      <button type="button" className="schedule-shift-edit" onClick={e => {e.stopPropagation(); setSelectedShiftId(shift.id);}}
        onDoubleClick={() => {setSelectedShiftId(shift.id); window.dispatchEvent(new CustomEvent('shiftbridge:smart-next',{detail:{shiftId:shift.id}}));}}>Edit</button>
    </div>
    <div className="schedule-shift-time">{fmtShortRange(shift.startTime,shift.endTime,shift)}</div>
    {workerLabel && <div className="schedule-shift-worker" title={assignedUid}>{workerLabel}</div>}
    <span className="schedule-shift-status" style={{background:statusBg,color:statusColor}}>{String(operational.operationalLabel || statusName || 'Open').toUpperCase()}</span>
    <div className="schedule-shift-meta">{modeName === 'direct' ? 'Direct Offer' : 'Open Pool'}{!workerLabel ? ' · Needs assignment' : ''}</div>
    {isTracking && <div className="schedule-shift-meta">{trackingDistance || trackingMeta}</div>}
    {footerText && <div className={'schedule-shift-meta'+(isOverdueOpen || isCritical ? ' schedule-shift-critical' : isUrgent ? ' schedule-shift-urgent' : '')}>{footerText}</div>}
  </article>;
}
